import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import Button from "components/button";
import NavbarAuth from "components/navBar";
import BirthdayDatePicker from "components/datePicker/birthdayDatePicker";
import { dateToEpoch, getAge } from "helper/helper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LandingPassword from "./Forms/LandingPassword";
import PreferenceQualification from "../formRegister/Forms/preferenceQualification";
import UseContext from "hooks/useContext";

const STATUS = {
  LOADING: "loading",
  VALID: "valid",
  EXPIRED: "expired",
  USED: "used",
  NOT_FOUND: "not_found",
  ERROR: "error",
};

const FormRegisterLanding = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { updateUser } = UseContext();
  const [sessionStatus, setSessionStatus] = useState(STATUS.LOADING);
  const [sessionData, setSessionData] = useState(null);
  const [page, setPage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [xsrfToken, setXsrfToken] = useState(null);
  const [userData, setUserData] = useState({
    password: "",
    confirmPassword: "",
    birthday: "",
    phone: "",
    preferenceQualifications: [],
  });
  const [validFormPassword, setValidFormPassword] = useState(false);
  const [validFormBirthday, setValidFormBirthday] = useState(false);
  const [dateSelect, setDateSelect] = useState();
  const [validAge, setValidAge] = useState(false);
  const [validFormPreferenceQualification, setValidFormPreferenceQualification] =
    useState(false);

  useEffect(() => {
    if (!token) {
      setSessionStatus(STATUS.NOT_FOUND);
      return;
    }

    axios
      .get(
        `${process.env.REACT_APP_API_URL}public/landing-registration/${token}`
      )
      .then((res) => {
        setSessionData(res.data);
        setUserData((prev) => ({
          ...prev,
          phone: res.data.phone || "",
        }));
        setSessionStatus(STATUS.VALID);
      })
      .catch((err) => {
        const code = err.response?.status;
        if (code === 410) setSessionStatus(STATUS.EXPIRED);
        else if (code === 400) setSessionStatus(STATUS.USED);
        else if (code === 404) setSessionStatus(STATUS.NOT_FOUND);
        else setSessionStatus(STATUS.ERROR);
      });
  }, [token]);

  useEffect(() => {
    if (dateSelect) {
      const calculatedAge = getAge(dateSelect);
      if (calculatedAge >= 18) {
        setUserData((prev) => ({
          ...prev,
          birthday: format(dateSelect, "dd-MM-yyyy"),
        }));
        setValidAge(true);
        setValidFormBirthday(true);
      } else {
        setValidAge(false);
        setValidFormBirthday(false);
      }
    } else {
      setValidFormBirthday(false);
    }
  }, [dateSelect]);

  const formTitles = [
    "Finalisez votre inscription",
    "Date de naissance",
    "Votre numéro de téléphone",
    "Quels postes recherchez-vous ?",
    "Inscription finalisée !",
  ];

  const formDescriptions = [
    "Choisissez un mot de passe pour accéder à votre compte Jobbiz.",
    "Nous avons besoin de votre date de naissance pour finaliser votre profil.",
    "Ce numéro a été renseigné lors de votre candidature.",
    null,
    null,
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validFormPreferenceQualification || isSubmitting) return;

    setIsSubmitting(true);
    const birthday = userData.birthday.split("-").reverse().join("-");

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}public/landing-registration/${token}/complete`,
        {
          password: userData.password,
          confirmPassword: userData.confirmPassword,
          birthday,
          preferenceQualifications: userData.preferenceQualifications,
        }
      );
      if (data?.xsrfToken) {
        updateUser(data.xsrfToken, data.user?.role);
        setXsrfToken(data.xsrfToken);
      }
      setPage(4);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Une erreur est survenue. Réessayez plus tard.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const PageDisplay = () => {
    if (page === 0) {
      return (
        <LandingPassword
          userData={userData}
          setUserData={setUserData}
          setValidFormPassword={setValidFormPassword}
        />
      );
    }
    if (page === 1) {
      return (
        <div className="rounded-md -space-y-px">
          <label className="block">
            <div className="ft-sm font-medium text-gray-500">
              Date de naissance <span className="text-red-500">*</span>
            </div>
            <div className="relative h-auto">
              <BirthdayDatePicker
                dateSelect={dateSelect}
                setDateSelect={setDateSelect}
              />
            </div>
          </label>
          {dateSelect && !validAge && (
            <p className="text-red-700 flex flex-row gap-4 items-center mt-2">
              Vous devez avoir 18 ans ou plus pour valider votre inscription.
            </p>
          )}
        </div>
      );
    }
    if (page === 2) {
      return (
        <div className="rounded-md bg-gray-50 border rounded-lg p-4">
          <div className="text-sm text-gray-500 mb-1">Numéro de téléphone</div>
          <div className="text-base text-gray-900 font-medium">
            {userData.phone || "—"}
          </div>
        </div>
      );
    }
    if (page === 3) {
      return (
        <PreferenceQualification
          userData={userData}
          setUserData={setUserData}
          setValidFormPreferenceQualification={setValidFormPreferenceQualification}
          publicMode
        />
      );
    }
    if (page === 4) {
      return (
        <div className="flex flex-col items-center gap-6 py-4">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center">
            <span className="text-3xl">🎉</span>
          </div>
          <p className="text-gray-700 text-center">
            Votre profil est complet. Accédez dès maintenant à votre espace
            Jobbiz.
          </p>
          <Button
            size="FULL"
            onClick={() => {
              if (xsrfToken) {
                window.location.assign(
                  `${process.env.REACT_APP_TOKEN_INTERIM}${xsrfToken}`
                );
              } else {
                navigate("/login/interimaire");
              }
            }}
          >
            Accéder à mon espace
          </Button>
        </div>
      );
    }
    return null;
  };

  const canGoNext = () => {
    if (page === 0) return validFormPassword;
    if (page === 1) return validFormBirthday;
    if (page === 2) return Boolean(userData.phone);
    return false;
  };

  // Progress bar (steps 1..3, excludes the password page):
  // completed step = filled, current step = highlighted, upcoming = muted.
  const stepBarClass = (step) => {
    if (page > step) return "bg-yellow-500";
    if (page === step) return "bg-yellow-300";
    return "bg-gray-200";
  };

  if (sessionStatus === STATUS.LOADING) {
    return (
      <div className="flex h-screen overflow-hidden">
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <NavbarAuth />
          <main className="h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500 mx-auto mb-4" />
              <p className="text-gray-500">Chargement de votre inscription...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (sessionStatus !== STATUS.VALID) {
    const errorConfig = {
      [STATUS.EXPIRED]: {
        title: "Lien expiré",
        message:
          "Ce lien de finalisation a expiré. Postulez à nouveau ou contactez le support.",
        icon: "⏰",
      },
      [STATUS.USED]: {
        title: "Inscription déjà finalisée",
        message: "Ce lien a déjà été utilisé. Connectez-vous à votre compte.",
        icon: "✅",
      },
      [STATUS.NOT_FOUND]: {
        title: "Lien introuvable",
        message: "Ce lien n'est pas valide. Vérifiez que vous avez copié le lien complet.",
        icon: "🔍",
      },
      [STATUS.ERROR]: {
        title: "Erreur",
        message: "Une erreur est survenue. Réessayez plus tard.",
        icon: "⚠️",
      },
    };
    const config = errorConfig[sessionStatus] || errorConfig[STATUS.ERROR];

    return (
      <div className="flex h-screen overflow-hidden">
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <NavbarAuth />
          <main className="h-screen flex items-center justify-center">
            <div className="max-w-md w-full space-y-8 text-center px-5">
              <div className="mx-auto mb-6 w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center">
                <span className="text-3xl">{config.icon}</span>
              </div>
              <h2 className="text-3xl font-medium text-[#111827]">{config.title}</h2>
              <p className="text-gray-500">{config.message}</p>
              <Button size="FULL" onClick={() => navigate("/login/interimaire")}>
                Se connecter
              </Button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const greeting = sessionData?.firstname
    ? `Bonjour ${sessionData.firstname},`
    : "Bonjour,";

  return (
    <>
      <div className="flex h-screen flex-col">
        <div className="relative flex flex-col flex-1 min-h-0">
          <NavbarAuth />
          <main className="flex-1 overflow-y-auto">
            <div
              className={`w-full max-w-[520px] mx-auto px-5 py-6 sm:py-12 flex flex-col gap-4 min-h-full justify-center ${
                page > 0 && page < 4 ? "pb-28 sm:pb-12" : ""
              }`}
            >
              <div className="text-left flex flex-col gap-2">
                <p className="text-gray-500 text-sm">{greeting}</p>
                <h1 className="text-2xl sm:text-3xl font-medium text-[#111827]">
                  {formTitles[page]}
                </h1>
                {formDescriptions[page] && (
                  <p className="text-gray-500 text-sm sm:text-base">
                    {formDescriptions[page]}
                  </p>
                )}
                {sessionData?.email && page === 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 mt-1">
                    Email : <span className="font-medium">{sessionData.email}</span>
                  </div>
                )}
                {sessionData?.pendingMissions?.length > 0 && page === 0 && (
                  <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 text-sm text-gray-700">
                    Candidature(s) en attente :{" "}
                    {sessionData.pendingMissions.map((m) => m.title).join(", ")}
                  </div>
                )}
              </div>

              <form className="space-y-4" onSubmit={handleSubmit}>
                {PageDisplay()}

                {page === 0 && (
                  <div className="flex justify-end">
                    <Button
                      size="FULL"
                      color={validFormPassword ? undefined : "DISABLED"}
                      onClick={(e) => {
                        e.preventDefault();
                        if (validFormPassword) setPage(1);
                      }}
                    >
                      Suivant
                    </Button>
                  </div>
                )}

                {page > 0 && page < 4 && (
                  <div className="hidden sm:flex gap-2 w-full pt-2">
                    {[1, 2, 3].map((step) => (
                      <div
                        key={step}
                        className={`h-1 rounded-lg w-full ${stepBarClass(step)}`}
                      />
                    ))}
                  </div>
                )}

                {page > 0 && page < 4 && (
                  <div className="hidden sm:flex gap-3">
                    {page > 0 && (
                      <Button
                        size="LG"
                        color="SECONDARY"
                        onClick={(e) => {
                          e.preventDefault();
                          setPage((p) => p - 1);
                        }}
                      >
                        Retour
                      </Button>
                    )}
                    {page === 3 ? (
                      <Button
                        size="FULL"
                        color={
                          validFormPreferenceQualification && !isSubmitting
                            ? undefined
                            : "DISABLED"
                        }
                        onClick={(e) =>
                          validFormPreferenceQualification && handleSubmit(e)
                        }
                      >
                        {isSubmitting
                          ? "Finalisation..."
                          : `Confirmer (${userData.preferenceQualifications.length}/5)`}
                      </Button>
                    ) : (
                      <Button
                        size="FULL"
                        color={canGoNext() ? undefined : "DISABLED"}
                        onClick={(e) => {
                          e.preventDefault();
                          if (canGoNext()) setPage((p) => p + 1);
                        }}
                      >
                        Suivant
                      </Button>
                    )}
                  </div>
                )}
              </form>
            </div>
          </main>

          {page > 0 && page < 4 && (
            <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-5 py-4 z-50">
              <div className="max-w-[520px] mx-auto flex flex-col gap-3">
                <div className="flex gap-2 w-full">
                  {[1, 2, 3].map((step) => (
                    <div
                      key={step}
                      className={`h-1 rounded-lg w-full ${stepBarClass(step)}`}
                    />
                  ))}
                </div>
                <div className="flex gap-3">
                  <Button
                    size="LG"
                    color="SECONDARY"
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Retour
                  </Button>
                  {page === 3 ? (
                    <Button
                      size="FULL"
                      color={
                        validFormPreferenceQualification && !isSubmitting
                          ? undefined
                          : "DISABLED"
                      }
                      onClick={(e) =>
                        validFormPreferenceQualification && handleSubmit(e)
                      }
                    >
                      {isSubmitting
                        ? "Finalisation..."
                        : `Confirmer (${userData.preferenceQualifications.length}/5)`}
                    </Button>
                  ) : (
                    <Button
                      size="FULL"
                      color={canGoNext() ? undefined : "DISABLED"}
                      onClick={() => canGoNext() && setPage((p) => p + 1)}
                    >
                      Suivant
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <ToastContainer position="bottom-left" autoClose={3000} limit={2} />
    </>
  );
};

export default FormRegisterLanding;
